import React from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Typography
} from "@material-ui/core";
import copy from "copy-to-clipboard";
import useTranslate from "../../../../../../hooks/use-translate";
import { useSnackbar } from "material-ui-snackbar-provider";
import LinkIcon from "./icons/link";
import ShareIcon from "./icons/share";
import { styled } from "@material-ui/core/styles";
import useUserReferralCode from "../../../../../../hooks/use-user-referral-code";
import ReferralStepper from "./ReferralStepper";
import useReferralSuccessCount from "../../../../../../hooks/use-referral-success-count";
import ShareModal from "./ShareModal";
import { analyticsTrackEvent } from "../../../../../../services/analytics";
import { getReferralShareData } from "./util";
import { useShareBoard } from "./use-share-board";

export const CustomCopyButton = styled(ButtonBase)(({ theme }) => ({
  fontWeight: 600,
  fontSize: `1.2rem`,
  lineHeight: `1.6rem`,
  letterSpacing: `1.5px`,
  height: `24px`,
  borderRadius: `30px`,
  textTransform: `uppercase`,
  padding: theme.spacing(0, 2),
  marginLeft: theme.spacing(2)
}));

const Wrapper = styled(`div`)(({ theme }) => ({
  minHeight: `286px`,
  color: theme.palette.common.white,
  position: `relative`,
  zIndex: 1,
  overflow: "hidden",
  filter: `drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.15))`,
  backgroundSize: `cover`,
  borderRadius: `3px 3px 0 0`,
  display: `flex`,
  flexDirection: `column`,
  alignItems: `center`,
  backgroundImage: `url(https://www.datocms-assets.com/15671/1616527069-referral-bg-pc.jpg)`,
  backgroundPosition: "bottom left",
  [theme.breakpoints.up(`sm`)]: {
    borderRadius: `8px 8px 0 0`
  }
}));

const LoadingMask = styled(`div`)(() => ({
  position: `absolute`,
  zIndex: 1,
  display: `flex`,
  justifyContent: `center`,
  alignItems: `center`,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  background: `rgba(17, 32, 36, 0.63)`,
  backdropFilter: `blur(4px)`
}));

const ShareLinkWrapper = styled(`div`)(({ theme }) => ({
  width: `100%`,
  height: `48px`,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  display: `flex`,
  justifyContent: `space-between`,
  alignItems: `center`,
  background: `rgba(17, 32, 36, 0.63)`,
  boxShadow: `0px 4px 4px rgba(0, 0, 0, 0.13)`,
  backdropFilter: `blur(4px)`,
  [theme.breakpoints.up(`sm`)]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4)
  },
  "& b": {
    fontSize: `1.2rem`,
    lineHeight: `1.6rem`,
    letterSpacing: `1.5px`,
    textAlign: `left`,
    flex: `1 1 auto`,
    marginLeft: theme.spacing(2),
    textTransform: `uppercase`
  }
}));

const CustomShareButton = styled(Button)(({ theme }) => ({
  fontSize: `1.2rem`,
  lineHeight: `1.6rem`,
  height: `46px`,
  borderRadius: `0 0 6px 6px`,
  [theme.breakpoints.up(`sm`)]: {
    borderRadius: `0 0 8px 8px`
  },
  "& svg": {
    marginRight: theme.spacing(1)
  }
}));

const TitleWrapper = styled(`div`)(({ theme }) => ({
  padding: theme.spacing(3, 2, 0),
  color: theme.palette.common.white,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textShadow: `0px 4px 16px rgba(0, 0, 0, 0.5)`
}));

export default function ReferralSection() {
  const t = useTranslate();
  const snackbar = useSnackbar();
  const { data: referralCode, isLoading } = useUserReferralCode();
  const { data: referralSuccessCount } = useReferralSuccessCount();
  const shareData = getReferralShareData(referralCode);
  const {
    openShareBoard,
    isCustomShareModalOpen,
    handelCustomShareModalClose
  } = useShareBoard(shareData);

  const handleCopy = () => {
    copy(shareData.url);
    snackbar.showMessage(t("user.profile.section.referral.copysuccess"));
  };

  const handleShare = async () => {
    openShareBoard();
  };

  return (
    <div>
      <Wrapper>
        {isLoading && (
          <LoadingMask>
            <CircularProgress size={16} />
          </LoadingMask>
        )}

        {!referralSuccessCount && (
          <TitleWrapper>
            <Typography variant="h5" color="inherit" align="center">
              Refer 3 friends to get a
            </Typography>
            <Typography variant="h5" color="primary" align="center">
              Reward pod for $1
            </Typography>
          </TitleWrapper>
        )}
        {referralSuccessCount > 0 && referralSuccessCount < 3 && (
          <TitleWrapper>
            <Typography variant="h5" color="inherit" align="center">
              Refer {3 - referralSuccessCount} more friend
              {3 - referralSuccessCount > 1 ? "s" : ""} to get a
            </Typography>
            <Typography variant="h5" color="primary" align="center">
              Reward pod for $1
            </Typography>
          </TitleWrapper>
        )}

        {referralSuccessCount >= 3 && (
          <TitleWrapper>
            <Typography variant="h5" color="inherit" align="center">
              Refer {3 - (referralSuccessCount % 3)} more friend
              {3 - (referralSuccessCount % 3) > 1 ? "s" : ""}, get
            </Typography>
            <Typography variant="h5" color="inherit" align="center">
              another{" "}
              <Typography
                component="span"
                variant="h5"
                color="primary"
                align="center"
              >
                Reward pod for $1
              </Typography>
            </Typography>
          </TitleWrapper>
        )}

        <ReferralStepper count={referralSuccessCount} />
        <Box mt={1} mb={1.5} ml={5} mr={5} maxWidth={{ md: `416px` }}>
          <Typography variant="body2" color="inherit" align="center">
            {t("user.profile.section.referral.inviteFriends")}
          </Typography>
        </Box>
        <ShareLinkWrapper>
          <Box
            display={{
              xs: `none`,
              md: `block`
            }}
          >
            <LinkIcon style={{ verticalAlign: `bottom` }} />
            <b>{t("user.profile.section.referral.link")}</b>
          </Box>
          <Typography
            noWrap
            variant="body2"
            color="primary"
            className="hide-in-percy"
          >
            {referralCode && shareData.prettyLink}
          </Typography>
          <CustomCopyButton
            onClick={handleCopy}
            style={{
              backgroundColor: `rgba(44, 70, 79, 0.4)`
            }}
          >
            {t("user.profile.section.referral.copy")}
          </CustomCopyButton>
        </ShareLinkWrapper>
      </Wrapper>
      <CustomShareButton
        variant="contained"
        fullWidth
        className="bold"
        onClick={handleShare}
        color="primary"
      >
        <ShareIcon />
        {t("user.profile.section.referral.share")}
      </CustomShareButton>
      <ShareModal
        shareData={shareData}
        open={isCustomShareModalOpen}
        copy={handleCopy}
        close={handelCustomShareModalClose}
      />
    </div>
  );
}
